import { ChangeDetectionStrategy, Component, DestroyRef, computed, effect, inject, signal } from '@angular/core';
import { DecimalPipe } from '@angular/common';
import { UploadService } from '../../../core/services/upload.service';
import { UploadItem, UploadStatus } from '../../../core/services/upload.types';
import { FileCategory } from '../../models';

const CATEGORY_ICON: Record<FileCategory, string> = {
	'video': 'pi pi-video',
	'mask': 'pi pi-eye',
	'video-mask': 'pi pi-eye-slash',
	'images': 'pi pi-image',
	'fonts': 'pi pi-palette',
	'audios': 'pi pi-volume-up',
	'subtitles': 'pi pi-language',
};

const AUTO_COLLAPSE_DELAY_MS = 10_000;
const CANCEL_ALL_CONFIRM_THRESHOLD = 10;

/** One row in the rendered list: either a standalone upload or a group header
 * (with its child variants flattened below when expanded). The template pivots
 * off `kind` — group headers get the aggregate progress + group-level controls,
 * items get per-file progress. */
export type PanelRow =
	| { kind: 'item'; item: UploadItem }
	| { kind: 'group'; groupId: string; groupName: string; groupCategory: FileCategory; children: UploadItem[] };

@Component({
	selector: 'app-upload-panel',
	changeDetection: ChangeDetectionStrategy.OnPush,
	imports: [DecimalPipe],
	templateUrl: './upload-panel.html',
	styleUrl: './upload-panel.scss',
	host: {
		'class': 'upload-panel',
		'[class.is-collapsed]': 'collapsed()',
		'[class.is-hidden]': '!hasItems()',
	},
})
export class UploadPanelComponent {
	protected readonly uploadService = inject(UploadService);
	private readonly destroyRef = inject(DestroyRef);

	protected readonly collapsed = signal(true);
	private autoCollapseTimer: ReturnType<typeof setTimeout> | null = null;

	/** Per-group expand state (D1 — collapsed by default). Keyed by groupId.
	 * Groups not in the set are collapsed; presence = expanded. */
	protected readonly expandedGroups = signal<Set<string>>(new Set());

	protected readonly hasItems = computed(() => this.uploadService.items().length > 0);

	/** Derived rendering plan — standalone items interleaved with group headers.
	 * The order follows first-appearance of each group/standalone, which matches
	 * enqueue order. When a group is expanded, its children are inlined under it
	 * in order; otherwise the header row alone represents the group. */
	protected readonly rows = computed<PanelRow[]>(() => {
		const items = this.uploadService.items();
		const expanded = this.expandedGroups();
		const seenGroups = new Set<string>();
		const childrenByGroup = new Map<string, UploadItem[]>();
		for (const item of items) {
			if (!item.groupId) continue;
			const list = childrenByGroup.get(item.groupId) ?? [];
			list.push(item);
			childrenByGroup.set(item.groupId, list);
		}
		// Build one "unit" per top-level entry (a standalone item, or a group
		// header plus its expanded children) in enqueue order, then reverse the
		// units so the newest upload shows at the top. Reversing whole units —
		// rather than the flat row list — keeps each group header above its own
		// children. Display-only: `items()` stays in FIFO order for queue
		// processing.
		const units: PanelRow[][] = [];
		for (const item of items) {
			if (item.groupId) {
				if (seenGroups.has(item.groupId)) continue;
				seenGroups.add(item.groupId);
				const children = childrenByGroup.get(item.groupId) ?? [];
				const head = children[0];
				const groupCategory = head.groupCategory ?? head.category;
				const groupName = head.groupName ?? 'Variant group';
				const unit: PanelRow[] = [{ kind: 'group', groupId: item.groupId, groupName, groupCategory, children }];
				if (expanded.has(item.groupId)) {
					for (const child of children) unit.push({ kind: 'item', item: child });
				}
				units.push(unit);
			} else {
				units.push([{ kind: 'item', item }]);
			}
		}
		units.reverse();
		return units.flat();
	});

	protected readonly isAllTerminal = computed(() => {
		const items = this.uploadService.items();
		return items.length > 0 && !this.uploadService.isActive();
	});

	/** Retriable failures only — validation errors (nonRetriable) are terminal
	 * and should not drive the "Retry all failed" footer button. */
	protected readonly hasFailures = computed(() =>
		this.uploadService.items().some(i => i.status() === 'error' && !i.nonRetriable),
	);

	protected readonly canCancelAll = computed(() => this.uploadService.isActive());

	constructor() {
		// Reset to collapsed whenever the panel re-appears from empty.
		effect(() => {
			if (!this.hasItems()) {
				this.clearAutoCollapseTimer();
				this.collapsed.set(true);
			}
		});

		// Auto-collapse 10s after the queue drains (only if still items in list).
		effect(() => {
			const active = this.uploadService.isActive();
			const itemsLen = this.uploadService.items().length;
			if (!active && itemsLen > 0) {
				this.scheduleAutoCollapse();
			} else {
				this.clearAutoCollapseTimer();
			}
		});

		// Force-expand on demand (currently triggered by Replace failures, which
		// have no placeholder in the file-browser grid and would otherwise be
		// invisible). The first read seeds the effect; subsequent bumps fire it.
		let lastExpandRequest = this.uploadService.panelExpandRequest();
		effect(() => {
			const current = this.uploadService.panelExpandRequest();
			if (current === lastExpandRequest) return;
			lastExpandRequest = current;
			this.clearAutoCollapseTimer();
			this.collapsed.set(false);
		});

		this.destroyRef.onDestroy(() => this.clearAutoCollapseTimer());
	}

	protected toggleCollapsed(): void {
		this.clearAutoCollapseTimer();
		this.collapsed.update(c => !c);
	}

	protected onCancelAll(): void {
		const activeCount = this.uploadService.items().filter(i =>
			i.status() === 'queued' || i.status() === 'probing' || i.status() === 'uploading',
		).length;
		if (activeCount === 0) return;
		if (activeCount > CANCEL_ALL_CONFIRM_THRESHOLD) {
			const ok = window.confirm(`You have ${activeCount} uploads in progress. Cancel them all?`);
			if (!ok) return;
		}
		this.uploadService.cancelAll(true);
	}

	protected onRetryAll(): void {
		this.uploadService.retryAllFailed();
	}

	protected onDismiss(): void {
		this.uploadService.dismiss();
	}

	protected onCancelItem(item: UploadItem): void {
		this.uploadService.cancel(item.id);
	}

	protected onRetryItem(item: UploadItem): void {
		this.uploadService.retry(item.id);
	}

	protected iconFor(item: UploadItem): string {
		return CATEGORY_ICON[item.category] ?? 'pi pi-file';
	}

	protected iconForCategory(category: FileCategory): string {
		return CATEGORY_ICON[category] ?? 'pi pi-file';
	}

	protected isGroupExpanded(groupId: string): boolean {
		return this.expandedGroups().has(groupId);
	}

	protected toggleGroup(groupId: string, event: MouseEvent): void {
		event.stopPropagation();
		this.expandedGroups.update(set => {
			const next = new Set(set);
			if (next.has(groupId)) next.delete(groupId);
			else next.add(groupId);
			return next;
		});
	}

	/** Mean of children's progress (D2). Done children count as 100% so the bar
	 * reaches 100% on completion; errored/cancelled children contribute their
	 * last-known progress (typically 0) to keep the bar from regressing. */
	protected groupProgress(children: UploadItem[]): number {
		if (!children.length) return 0;
		let sum = 0;
		for (const c of children) {
			sum += c.status() === 'done' ? 100 : c.progress();
		}
		return Math.round(sum / children.length);
	}

	protected groupDoneCount(children: UploadItem[]): number {
		return children.filter(c => c.status() === 'done').length;
	}

	protected groupHasActive(children: UploadItem[]): boolean {
		const active: UploadStatus[] = ['queued', 'probing', 'uploading'];
		return children.some(c => active.includes(c.status()));
	}

	/** A group can be retried when at least one child failed with a retriable
	 * error (not a `nonRetriable` validation failure). */
	protected groupHasRetriableFailures(children: UploadItem[]): boolean {
		return children.some(c => c.status() === 'error' && !c.nonRetriable);
	}

	protected groupErrorCount(children: UploadItem[]): number {
		return children.filter(c => c.status() === 'error').length;
	}

	protected onCancelGroup(groupId: string, event: MouseEvent): void {
		event.stopPropagation();
		this.uploadService.cancelGroup(groupId);
	}

	protected onRetryGroup(groupId: string, event: MouseEvent): void {
		event.stopPropagation();
		this.uploadService.retryGroup(groupId);
	}

	/** Whole-number seconds remaining until the next retry attempt. */
	protected retrySecondsFor(item: UploadItem): number {
		const ms = item.retryAfterMs();
		if (ms == null) return 0;
		return Math.max(1, Math.ceil(ms / 1000));
	}

	/** Total failed count (includes non-retriable validation errors) for the
	 * header summary — user still wants to know "N failed" regardless of
	 * whether retry is possible. */
	protected failedCount = computed(() =>
		this.uploadService.items().filter(i => i.status() === 'error').length,
	);

	/** Retriable-only count driving the "Retry all failed (N)" footer button. */
	protected retriableFailedCount = computed(() =>
		this.uploadService.items().filter(i => i.status() === 'error' && !i.nonRetriable).length,
	);

	private scheduleAutoCollapse(): void {
		this.clearAutoCollapseTimer();
		this.autoCollapseTimer = setTimeout(() => {
			this.autoCollapseTimer = null;
			this.collapsed.set(true);
		}, AUTO_COLLAPSE_DELAY_MS);
	}

	private clearAutoCollapseTimer(): void {
		if (this.autoCollapseTimer) {
			clearTimeout(this.autoCollapseTimer);
			this.autoCollapseTimer = null;
		}
	}
}
